// src/modules/app-views/leads/pages/Init/modals/LeadsParametersModal/tabs/DataSourcesTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect, useRef } from 'react';
import { Button, Flex, Popconfirm, Tag, Switch, Tooltip } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  CopyOutlined,
  CheckOutlined,
  ApiOutlined,
  DisconnectOutlined,
} from '@ant-design/icons';
import { FacebookFilled } from '@ant-design/icons';
import SimpleTable from 'components/SimpleComponents/SimpleTable';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import AddDataSourceModal from '../modals/AddDataSourceModal';
import MetaConnectModal from '../modals/MetaConnectModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DataSourcesTab = ({ loading: parentLoading }) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const tableWrapperRef = useRef(null);

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const TYPE_COLORS = {
    'landing-page': { color: 'blue', label: 'Landing Page' },
    'meta-ads': { color: 'purple', label: 'Meta Ads' },
    'form-submission': { color: 'cyan', label: 'Form' },
    'api-webhook': { color: 'orange', label: 'Webhook' },
    'import': { color: 'green', label: 'Import' },
    'other': { color: 'default', label: 'Other' },
  };

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [dataSources, setDataSources] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);
  const [showMetaModal, setShowMetaModal] = useState(false);
  const [connectingRecord, setConnectingRecord] = useState(null);

  const isLoading = parentLoading || fetchLoading;

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    fetchDataSources();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply overscroll-behavior directly on the AntD internal scroll container
  // to prevent rubber-band effect at the horizontal scroll boundaries
  useEffect(() => {
    if (!tableWrapperRef.current) return;
    const scrollEl = tableWrapperRef.current.querySelector('.ant-table-body');
    if (scrollEl) scrollEl.style.overscrollBehaviorX = 'contain';
  }, [dataSources]);

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const fetchDataSources = useCallback(async () => {
    setFetchLoading(true);
    try {
      const [response] = await axiosService.get({ url: API.DATA_SOURCES.PAGINATION });
      const docs = Array.isArray(response)
        ? response
        : Array.isArray(response?.docs)
          ? response.docs
          : response
            ? [response]
            : [];
      setDataSources(docs);
    } catch (error) {
      console.error('[DataSourcesTab] Fetch error:', error);
    } finally {
      setFetchLoading(false);
    }
  }, []);

  const handleAdd = useCallback(() => {
    setEditingRecord(null);
    setShowAddModal(true);
  }, []);

  const handleEdit = useCallback(record => {
    setEditingRecord(record);
    setShowAddModal(true);
  }, []);

  // Optimistic update: add new doc or update existing one in local state
  const handleModalSuccess = useCallback((savedDoc, isEditing) => {
    if (!savedDoc) {
      fetchDataSources();
      return;
    }
    if (isEditing) {
      setDataSources(prev => prev.map(d => d._id === savedDoc._id ? { ...d, ...savedDoc } : d));
    } else {
      setDataSources(prev => [...prev, savedDoc]);
    }
  }, [fetchDataSources]);

  const handleDelete = useCallback(async record => {
    // Optimistic remove
    setDataSources(prev => prev.filter(d => d._id !== record._id));
    try {
      const url = API.DATA_SOURCES.URL.replace(':_id?', record._id);
      await axiosService.delete({ url });
      appService.message({ flag: 's', text: 'removed_successfully' });
    } catch (error) {
      console.error('[DataSourcesTab] Delete error:', error);
      appService.message({ flag: 'e', text: 'error_removing' });
      // Revert on error
      fetchDataSources();
    }
  }, [fetchDataSources]);

  const handleToggleActive = useCallback(async (record, checked) => {
    // Optimistic update
    setDataSources(prev => prev.map(d => d._id === record._id ? { ...d, active: checked } : d));
    try {
      const url = API.DATA_SOURCES.URL.replace(':_id?', record._id);
      await axiosService.put({ url, data: { active: checked } });
      appService.message({ flag: 's', text: 'saved_successfully' });
    } catch (error) {
      console.error('[DataSourcesTab] Toggle error:', error);
      appService.message({ flag: 'e', text: 'error_saving' });
      // Revert on error
      setDataSources(prev => prev.map(d => d._id === record._id ? { ...d, active: !checked } : d));
    }
  }, []);

  const handleCopyToken = useCallback(token => {
    navigator.clipboard.writeText(token).then(() => {
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    });
  }, []);

  const handleConnectMeta = useCallback(record => {
    setConnectingRecord(record);
    setShowMetaModal(true);
  }, []);

  const handleMetaConnectSuccess = useCallback(updatedRecord => {
    setDataSources(prev =>
      prev.map(d => d._id === updatedRecord._id ? { ...d, ...updatedRecord } : d),
    );
  }, []);

  const handleDisconnectMeta = useCallback(async record => {
    try {
      await axiosService.post({
        url: API.DATA_SOURCES.META_UNSUBSCRIBE,
        data: { dataSourceId: record._id },
      });
      // Clear meta fields in local state
      setDataSources(prev =>
        prev.map(d =>
          d._id === record._id
            ? { ...d, metaPageId: null, metaPageName: null, metaSubscribedAt: null }
            : d,
        ),
      );
      appService.message({ flag: 's', text: 'removed_successfully' });
    } catch (error) {
      console.error('[DataSourcesTab] Disconnect Meta error:', error);
      appService.message({ flag: 'e', text: 'error_saving' });
    }
  }, []);

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  const columns = [
    {
      title: translateX('source'),
      dataIndex: 'name',
      width: 200,
      render: (text, r) => (
        <div className="d-flex flex-column" style={{ minWidth: 0 }}>
          <span className="font-weight-semibold">{text}</span>
          {r.description && (
            <span className="text-muted" style={{ fontSize: 11 }}>
              {r.description}
            </span>
          )}
          {r.url && (
            <a
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted"
              style={{
                fontSize: 11,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              onClick={e => e.stopPropagation()}
            >
              <LinkOutlined style={{ marginRight: 3 }} />
              {r.url}
            </a>
          )}
        </div>
      ),
    },
    {
      title: 'Token',
      dataIndex: 'token',
      width: 150,
      render: token => {
        if (!token) return '—';
        const masked = `${token.slice(0, 8)}...${token.slice(-4)}`;
        const isCopied = copiedToken === token;
        return (
          <Tooltip title={isCopied ? translateX('copied') : translateX('copy')}>
            <span
              onClick={() => handleCopyToken(token)}
              style={{
                fontFamily: 'monospace',
                fontSize: 11,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                color: isCopied ? '#52c41a' : 'inherit',
              }}
            >
              {isCopied ? <CheckOutlined /> : <CopyOutlined />}
              {masked}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: translateX('source_type'),
      dataIndex: 'type',
      width: 120,
      align: 'center',
      render: type => {
        const config = TYPE_COLORS[type] || { color: 'default', label: type || '—' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: 'Meta',
      dataIndex: 'metaPageId',
      width: 160,
      align: 'center',
      render: (metaPageId, record) => {
        if (metaPageId) {
          return (
            <Flex vertical align="center" gap={2}>
              <Flex align="center" gap={4} style={{ color: '#52c41a' }}>
                <FacebookFilled style={{ color: '#1877F2', fontSize: 14 }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: '#1877F2' }}>
                  {record.metaPageName || metaPageId}
                </span>
              </Flex>
              <Popconfirm
                title={translateX('meta_disconnect_confirm')}
                onConfirm={() => handleDisconnectMeta(record)}
                okText={translateX('yes')}
                cancelText={translateX('no')}
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DisconnectOutlined />}
                  disabled={isLoading}
                  style={{ fontSize: 11, height: 'auto', padding: '0 2px' }}
                >
                  {translateX('disconnect')}
                </Button>
              </Popconfirm>
            </Flex>
          );
        }

        return (
          <Tooltip title={translateX('connect_meta_page_tooltip')}>
            <Button
              type="dashed"
              size="small"
              icon={<ApiOutlined />}
              onClick={() => handleConnectMeta(record)}
              disabled={isLoading}
              style={{ fontSize: 11 }}
            >
              {translateX('connect')}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: translateX('leads'),
      dataIndex: 'totalLeads',
      width: 70,
      align: 'center',
      render: val => val || 0,
    },
    {
      title: translateX('active'),
      dataIndex: 'active',
      width: 70,
      align: 'center',
      fixed: 'right',
      render: (active, record) => (
        <Switch
          size="small"
          checked={active}
          onChange={checked => handleToggleActive(record, checked)}
          disabled={isLoading}
        />
      ),
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 80,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Flex vertical align="center" gap={2}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={isLoading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={isLoading} />
          </Popconfirm>
        </Flex>
      ),
    },
  ];

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <Flex vertical style={{ height: '100%' }}>
      <div ref={tableWrapperRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <SimpleTable
          rowKey="_id"
          columns={columns}
          dataSource={dataSources}
          pagination={false}
          size="small"
          loading={isLoading}
          locale={{ emptyText: translateX('no_data') }}
          scroll={{ x: 860, y: 'calc(80vh - 240px)' }}
        />
      </div>

      <Flex justify="flex-end" className="mt-3" style={{ flexShrink: 0 }}>
        <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd} disabled={isLoading}>
          {translateX('add_data_source')}
        </Button>
      </Flex>

      <AddDataSourceModal
        show={showAddModal}
        setShow={setShowAddModal}
        editingRecord={editingRecord}
        onSuccess={handleModalSuccess}
      />

      {connectingRecord && (
        <MetaConnectModal
          show={showMetaModal}
          setShow={setShowMetaModal}
          dataSource={connectingRecord}
          onSuccess={handleMetaConnectSuccess}
        />
      )}
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DataSourcesTab;
